import React, { useState } from "react";
import styled from "styled-components";
import { sharedLib } from "@shared/services/shared.service";
import { ScrollReveal } from "features/whop/whopLanding/components/animations.tsx";

interface DemoFormSectionProps {}

export const DemoFormSection: React.FC<DemoFormSectionProps> = () => {
  const [requestDemo, { isLoading }] =
    sharedLib.sharedApi.settings.useRequestAssetManagerDemoMutation();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    number_of_users: "1-5",
    estimated_aum: "",
    goals_and_challenges: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.first_name.trim()) newErrors.first_name = "Required";
    if (!formData.last_name.trim()) newErrors.last_name = "Required";
    if (!formData.email.trim()) newErrors.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email";
    if (!formData.goals_and_challenges.trim())
      newErrors.goals_and_challenges = "Required";
    return newErrors;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      await requestDemo({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        number_of_clients: formData.number_of_users,
        estimated_aum: formData.estimated_aum || undefined,
        goals_and_challenges: formData.goals_and_challenges,
      }).unwrap();
      setSubmitted(true);
    } catch {}
  };

  if (submitted) {
    return (
      <SectionOuter>
        <Section>
          <ScrollReveal>
            <FormCard>
              <SuccessContent>
                <SuccessIconCircle>
                  <SuccessCheckIcon />
                </SuccessIconCircle>
                <SuccessTitle>Thank you for your submission!</SuccessTitle>
                <SuccessSubtitle>
                  Our team will review your request and reach out shortly.
                </SuccessSubtitle>
              </SuccessContent>
            </FormCard>
          </ScrollReveal>
        </Section>
      </SectionOuter>
    );
  }

  return (
    <SectionOuter>
      <Section>
        <ScrollReveal>
          <FormCard>
            <FormTitle>Explore Cornix for Whop Communities</FormTitle>
            <FormSubtitle>
              Request a demo to see how Cornix helps your community members
              automate signal execution, so they never miss a trade.
            </FormSubtitle>
            <Form onSubmit={handleSubmit} noValidate>
              <NameRow>
                <FieldGroup>
                  <Label>
                    First Name<Required>*</Required>
                  </Label>
                  <Input
                    type="text"
                    name="first_name"
                    placeholder="Your first name"
                    value={formData.first_name}
                    onChange={handleChange}
                    $hasError={!!errors.first_name}
                  />
                  {errors.first_name && (
                    <ErrorText>{errors.first_name}</ErrorText>
                  )}
                </FieldGroup>
                <FieldGroup>
                  <Label>
                    Last Name<Required>*</Required>
                  </Label>
                  <Input
                    type="text"
                    name="last_name"
                    placeholder="Your last name"
                    value={formData.last_name}
                    onChange={handleChange}
                    $hasError={!!errors.last_name}
                  />
                  {errors.last_name && (
                    <ErrorText>{errors.last_name}</ErrorText>
                  )}
                </FieldGroup>
              </NameRow>
              <FieldGroup>
                <Label>
                  Email<Required>*</Required>
                </Label>
                <Input
                  type="email"
                  name="email"
                  placeholder="Your business email address"
                  value={formData.email}
                  onChange={handleChange}
                  $hasError={!!errors.email}
                />
                {errors.email && <ErrorText>{errors.email}</ErrorText>}
              </FieldGroup>
              <FieldGroup>
                <Label>
                  How many clients do you manage or plan to onboard?
                  <Required>*</Required>
                </Label>
                <Select
                  name="number_of_users"
                  value={formData.number_of_users}
                  onChange={handleChange}
                >
                  {NUMBER_OF_USERS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </FieldGroup>
              <FieldGroup>
                <Label>Estimated AUM (Assets Under Management)</Label>
                <Input
                  type="text"
                  name="estimated_aum"
                  placeholder="Please type your estimated value of assets under management"
                  value={formData.estimated_aum}
                  onChange={handleChange}
                  $hasError={false}
                />
              </FieldGroup>
              <FieldGroup>
                <Label>
                  Your top goals and challenges<Required>*</Required>
                </Label>
                <Textarea
                  name="goals_and_challenges"
                  placeholder="Please share what you're looking to achieve or automate with Cornix."
                  value={formData.goals_and_challenges}
                  onChange={handleChange}
                  $hasError={!!errors.goals_and_challenges}
                  rows={5}
                />
                {errors.goals_and_challenges && (
                  <ErrorText>{errors.goals_and_challenges}</ErrorText>
                )}
              </FieldGroup>
              <SubmitButton type="submit" disabled={isLoading}>
                {isLoading ? "SUBMITTING..." : "BOOK A DEMO"}
              </SubmitButton>
              <PrivacyNote>
                Your data will be processed in accordance with our{" "}
                <PrivacyLink
                  href="https://cornix.io/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </PrivacyLink>
                . By booking a demo, you agree to be contacted by the Cornix
                team for scheduling and follow-up.
              </PrivacyNote>
            </Form>
          </FormCard>
        </ScrollReveal>
      </Section>
    </SectionOuter>
  );
};

const SuccessCheckIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1e3a8a"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const NUMBER_OF_USERS_OPTIONS = ["1-5", "6-20", "21-50", "51-100", "100+"];

const SectionOuter = styled.div`
  background: ${(p) => (p.theme.isDarkMode ? "#0a0a0a" : "#F8FAFC")};
  border-top: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
  border-bottom: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
`;

const Section = styled.section`
  padding: 80px 24px;
  max-width: 960px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 56px 20px;
  }
  @media (max-width: 480px) {
    padding: 40px 16px;
  }
`;

const FormCard = styled.div`
  background: ${(p) => (p.theme.isDarkMode ? "#111827" : "#ffffff")};
  border-radius: 16px;
  padding: 48px;
  border: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"};
  box-shadow: 0 2px 16px
    ${(p) => (p.theme.isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.06)")};
  @media (max-width: 768px) {
    padding: 32px 24px;
  }
  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const FormTitle = styled.h2`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 700;
  color: ${(p) => (p.theme.isDarkMode ? "#ffffff" : "#0F172A")};
  margin: 0 0 12px;
  letter-spacing: -0.02em;
`;

const FormSubtitle = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${(p) => (p.theme.isDarkMode ? "#9CA3AF" : "#4B5563")};
  margin: 0 0 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NameRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${(p) => (p.theme.isDarkMode ? "#E5E7EB" : "#0F172A")};
`;

const Required = styled.span`
  color: #2563eb;
  margin-left: 2px;
`;

const Input = styled.input<{ $hasError: boolean }>`
  padding: 12px 16px;
  border-radius: 10px;
  border: 1.5px solid
    ${(p) =>
      p.$hasError
        ? "#ef4444"
        : p.theme.isDarkMode
        ? "rgba(255,255,255,0.12)"
        : "#E5E7EB"};
  background: ${(p) => (p.theme.isDarkMode ? "#1F2937" : "#F9FAFB")};
  color: ${(p) => (p.theme.isDarkMode ? "#ffffff" : "#0F172A")};
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s;
  &::placeholder {
    color: ${(p) => (p.theme.isDarkMode ? "#6B7280" : "#9CA3AF")};
  }
  &:focus {
    border-color: #2563eb;
    background: ${(p) => (p.theme.isDarkMode ? "#1F2937" : "#ffffff")};
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border-radius: 10px;
  border: 1.5px solid
    ${(p) => (p.theme.isDarkMode ? "rgba(255,255,255,0.12)" : "#E5E7EB")};
  background: ${(p) => (p.theme.isDarkMode ? "#1F2937" : "#F9FAFB")};
  color: ${(p) => (p.theme.isDarkMode ? "#ffffff" : "#0F172A")};
  font-size: 1rem;
  outline: none;
  cursor: pointer;
  appearance: auto;
  transition: border-color 0.2s;
  &:focus {
    border-color: #2563eb;
  }
`;

const Textarea = styled.textarea<{ $hasError: boolean }>`
  padding: 12px 16px;
  border-radius: 10px;
  border: 1.5px solid
    ${(p) =>
      p.$hasError
        ? "#ef4444"
        : p.theme.isDarkMode
        ? "rgba(255,255,255,0.12)"
        : "#E5E7EB"};
  background: ${(p) => (p.theme.isDarkMode ? "#1F2937" : "#F9FAFB")};
  color: ${(p) => (p.theme.isDarkMode ? "#ffffff" : "#0F172A")};
  font-size: 1rem;
  outline: none;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s;
  &::placeholder {
    color: ${(p) => (p.theme.isDarkMode ? "#6B7280" : "#9CA3AF")};
  }
  &:focus {
    border-color: #2563eb;
    background: ${(p) => (p.theme.isDarkMode ? "#1F2937" : "#ffffff")};
  }
`;

const ErrorText = styled.span`
  font-size: 0.8125rem;
  color: #ef4444;
`;

const SubmitButton = styled.button`
  padding: 16px;
  background: #2563eb;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  margin-top: 4px;
  &:hover:not(:disabled) {
    background: #1d4ed8;
    transform: scale(1.01);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PrivacyNote = styled.p`
  font-size: 0.875rem;
  color: ${(p) => (p.theme.isDarkMode ? "#6B7280" : "#9CA3AF")};
  line-height: 1.5;
  margin: 0;
`;

const PrivacyLink = styled.a`
  color: #2563eb;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  gap: 20px;
`;

const SuccessIconCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #dbeafe;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SuccessTitle = styled.h2`
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  color: ${(p) => (p.theme.isDarkMode ? "#ffffff" : "#0F172A")};
  margin: 0;
`;

const SuccessSubtitle = styled.p`
  font-size: 1rem;
  color: ${(p) => (p.theme.isDarkMode ? "#9CA3AF" : "#4B5563")};
  margin: 0;
`;
